import {
  createAllCacheEntriesUpdater,
  createAppQueryBuilder,
  createUpdateInfiniteDataRecipe,
  createUpdateMultipleItemsRecipe,
  isQueryFulfilledRejectionReason,
  isQueryFulfilledRejectionTimeoutError,
} from '@autone/utils';
import { type ThunkDispatch, type UnknownAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { customBaseQuery } from '../utils/custom-base-query';

import { isConcreteDimensionIdentifier } from './transforms/map-row-dimension-to-identifier';
import { transformEditInSeasonAggregationRequest } from './transforms/transform-edit-in-season-aggregation-request';
import { transformEditPreSeasonAggregationRequest } from './transforms/transform-edit-pre-season-aggregation-request';
import { transformInSeasonAggregationRequest } from './transforms/transform-in-season-aggregation-request';
import {
  transformEditInSeasonAggregationResponse,
  transformInSeasonAggregationResponse,
} from './transforms/transform-in-season-aggregation-response';
import { transformPreSeasonAggregationsRequest } from './transforms/transform-pre-season-aggregation-request';
import {
  transformEditPreSeasonAggregationResponse,
  transformPreSeasonAggregationResponse,
} from './transforms/transform-pre-season-aggregation-response';
import {
  type CreateInitialAllocationRecommendationsJobRequest,
  type CreateInitialAllocationRecommendationsJobResponse,
  type EditInSeasonAggregationResponse,
  type EditPreSeasonAggregationError,
  type EditPreSeasonAggregationResponse,
  type GetCustomerDataFeedStatusRequest,
  type GetCustomerDataFeedStatusResponse,
  type GetInSeasonAggregationsListInfiniteQueryPageParam,
  type GetInSeasonAggregationsListResponse,
  type GetInSeasonSkuLocationsCountRequest,
  type GetInSeasonSkuLocationsCountResponse,
  type GetLatestInitialAllocationRecommendationsJobRequest,
  type GetLatestInitialAllocationRecommendationsJobResponse,
  type GetPreSeasonAggregationsListInfiniteQueryPageParam,
  type GetPreSeasonAggregationsListResponse,
  type GetPreSeasonSkuLocationsCountRequest,
  type GetPreSeasonSkuLocationsCountResponse,
  type TransformedEditInSeasonAggregationRequest,
  type TransformedEditInSeasonAggregationResponse,
  type TransformedEditPreSeasonAggregationRequest,
  type TransformedEditPreSeasonAggregationResponse,
  type TransformedGetInSeasonAggregationsListRequest,
  type TransformedGetInSeasonAggregationsListResponse,
  type TransformedGetPreSeasonAggregationsListRequest,
  type TransformedGetPreSeasonAggregationsListResponse,
} from './types';
import {
  adjustRequestsInFlightRecipe,
  editInFlightFlagsRecipe,
  mergePatchedMetricsRecipe,
  setRemainingWhStockFetchingRecipe,
} from './utilities/edit-pre-season-recipes';
import { findInSeasonQueryArgsToInvalidate } from './utilities/find-in-season-query-args-to-invalidate';
import { findPreSeasonQueryArgsToInvalidate } from './utilities/find-pre-season-query-args-to-invalidate';
import { findPreSeasonRowsToRefresh } from './utilities/find-pre-season-rows-to-refresh';

const API_PREFIX = '/assortment';

// TODO: Remove this RTK timeout when AUT-5582 and AUT-5581 is completed
// currently 504s are not propagated to the client because of some CORS
// related headers missing on the response
const AGGREGATIONS_LIST_TIMEOUT_MS = 24_000;

const assortmentQueryBuilder = createAppQueryBuilder<
  'assortment',
  typeof API_PREFIX
>();

// Builds the request for the pre-season list route. Shared by the paginated
// list endpoint and the transient refreshPreSeasonAggregations endpoint that
// the post-edit silent refresh uses.
const preSeasonAggregationsListQueryFn = assortmentQueryBuilder(
  'post',
  '/aggregations/pre-season',
  transformPreSeasonAggregationsRequest,
);

/**
 * @description assortment redux api
 */
export const assortmentApi = createApi({
  reducerPath: 'assortmentApi',
  baseQuery: async (args, api, extraOptions) =>
    customBaseQuery(args, api, extraOptions, `v2${API_PREFIX}`),
  tagTypes: [
    'CUSTOMER_DATA_FEED_STATUS',
    'LATEST_JOB',
    'IN_SEASON_AGGREGATIONS_LIST',
    'PRE_SEASON_AGGREGATIONS_LIST',
  ],
  endpoints: (builder) => ({
    createInitialAllocationRecommendationsJob: builder.mutation<
      CreateInitialAllocationRecommendationsJobResponse,
      CreateInitialAllocationRecommendationsJobRequest
    >({
      query: assortmentQueryBuilder('post', '/jobs'),
    }),
    getLatestInitialAllocationRecommendationsJob: builder.query<
      GetLatestInitialAllocationRecommendationsJobResponse,
      GetLatestInitialAllocationRecommendationsJobRequest
    >({
      // hideSnackbar is forced on here because the BE returns 404 when no job
      // has ever run, and we don't want to surface that as an error to users
      query: (args) => ({
        ...assortmentQueryBuilder('post', '/jobs/latest')(args),
        extraArgs: { ...args?.extraArgs, hideSnackbar: true },
      }),
      providesTags: ['LATEST_JOB'],
    }),
    getCustomerDataFeedStatus: builder.query<
      GetCustomerDataFeedStatusResponse,
      GetCustomerDataFeedStatusRequest
    >({
      query: assortmentQueryBuilder('get', '/status'),
      providesTags: ['CUSTOMER_DATA_FEED_STATUS'],
    }),

    // ========================== PRE-SEASON AGGREGATIONS ==========================
    getPreSeasonSkuLocationsCount: builder.query<
      GetPreSeasonSkuLocationsCountResponse,
      GetPreSeasonSkuLocationsCountRequest
    >({
      query: (args) => ({
        ...assortmentQueryBuilder(
          'post',
          '/aggregations/pre-season/count',
        )(args),
        extraArgs: { ...args?.extraArgs, hideSnackbar: true },
      }),
    }),
    getPreSeasonAggregationsList: builder.infiniteQuery<
      TransformedGetPreSeasonAggregationsListResponse,
      TransformedGetPreSeasonAggregationsListRequest,
      GetPreSeasonAggregationsListInfiniteQueryPageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { query, ...rest }, pageParam: { cursor } }) => {
        return {
          ...preSeasonAggregationsListQueryFn({
            query: { ...query, cursor },
            ...rest,
          }),
          timeout: AGGREGATIONS_LIST_TIMEOUT_MS,
        };
      },
      transformResponse: (response: GetPreSeasonAggregationsListResponse) =>
        transformPreSeasonAggregationResponse(response),
      providesTags: (result, _, queryArg) => {
        if (!result) return ['PRE_SEASON_AGGREGATIONS_LIST'];
        return [
          'PRE_SEASON_AGGREGATIONS_LIST',
          {
            type: 'PRE_SEASON_AGGREGATIONS_LIST',
            id: JSON.stringify(queryArg),
          },
        ];
      },
    }),
    // Transient companion to getPreSeasonAggregationsList, used only by the
    // post-edit silent refresh in editPreSeasonAggregation. It is driven
    // via `initiate` with `subscribe: false`, so it never creates a
    // subscription or a loading state, and `keepUnusedDataFor: 0` drops the
    // cache entry as soon as the refresh has read it. Returns the raw response
    // — the refresh applies the item transform when it merges.
    refreshPreSeasonAggregations: builder.query<
      GetPreSeasonAggregationsListResponse,
      TransformedGetPreSeasonAggregationsListRequest
    >({
      query: (args) => ({
        ...preSeasonAggregationsListQueryFn(args),
        extraArgs: { hideSnackbar: true },
        timeout: AGGREGATIONS_LIST_TIMEOUT_MS,
      }),
      keepUnusedDataFor: 0,
    }),
    editPreSeasonAggregation: builder.mutation<
      TransformedEditPreSeasonAggregationResponse,
      TransformedEditPreSeasonAggregationRequest
    >({
      query: (args) => {
        const queryFn = assortmentQueryBuilder(
          'patch',
          '/aggregations/pre-season',
          transformEditPreSeasonAggregationRequest,
        );
        return {
          ...queryFn(args),
          timeout: AGGREGATIONS_LIST_TIMEOUT_MS,
        };
      },
      transformResponse: (response: EditPreSeasonAggregationResponse) =>
        transformEditPreSeasonAggregationResponse(response),
      onQueryStarted: async (mutationQueryArgs, mutationLifecycleApi) => {
        const { body } = mutationQueryArgs;
        const { queryFulfilled, dispatch } = mutationLifecycleApi;

        const edit = beginPreSeasonEdit();
        const updatePreSeasonEntries = createAllCacheEntriesUpdater(
          assortmentApi,
          'getPreSeasonAggregationsList',
          mutationLifecycleApi,
        );

        // edits for which no other values in the table depend on them, so they
        // drive their own independent cell spinner instead of the shared row
        const INDEPENDENT_LOADING_UPDATES = new Set([
          'min_ia_set_each',
          'target_ia_coverage_set_each',
        ]);
        const trackRequestsInFlight = !INDEPENDENT_LOADING_UPDATES.has(
          body.update.update_type,
        );

        if (body.selection.selection_type === 'include_list') {
          const editedRowIds = new Set(
            body.selection.include_list.map((row) =>
              JSON.stringify(row.filter(isConcreteDimensionIdentifier)),
            ),
          );

          const inFlightFlagPatches = updatePreSeasonEntries(
            editInFlightFlagsRecipe(editedRowIds, body.update),
          );
          if (trackRequestsInFlight) {
            updatePreSeasonEntries(
              adjustRequestsInFlightRecipe(editedRowIds, 1),
            );
          }

          let editSucceeded = false;
          try {
            const { data } = await queryFulfilled;
            updatePreSeasonEntries(mergePatchedMetricsRecipe(data.items));
            invalidateStalePreSeasonViews(
              mutationLifecycleApi,
              mutationQueryArgs,
            );
            editSucceeded = true;
          } catch (e) {
            recoverFromFailedPreSeasonEdit(dispatch, e);
          } finally {
            inFlightFlagPatches.forEach((patch) => patch.undo());
            if (trackRequestsInFlight) {
              updatePreSeasonEntries(
                adjustRequestsInFlightRecipe(editedRowIds, -1),
              );
            }
          }

          // The edit indirectly changes warehouse-stock metrics ("x after IA")
          // on other loaded rows served by the same warehouse, so they are
          // re-read and merged in place while their "after IA" cells show a
          // spinner. Single-row edits only; bulk edits keep their existing
          // behaviour.
          if (
            trackRequestsInFlight &&
            editSucceeded &&
            body.selection.include_list.length === 1
          ) {
            try {
              if (!edit.isLatest()) return;

              const { keptQueryArgs, rows, rowIds } =
                findPreSeasonRowsToRefresh(
                  mutationQueryArgs,
                  assortmentApi.util.selectCachedArgsForQuery(
                    mutationLifecycleApi.getState(),
                    'getPreSeasonAggregationsList',
                  ),
                  (queryArgs) =>
                    assortmentApi.endpoints.getPreSeasonAggregationsList.select(
                      queryArgs,
                    )(mutationLifecycleApi.getState()).data,
                );
              if (rows.length === 0) return;

              const refreshFetchingPatches = updatePreSeasonEntries(
                setRemainingWhStockFetchingRecipe(rowIds, true, editedRowIds),
              );
              try {
                const { data, error } = await dispatch(
                  assortmentApi.endpoints.refreshPreSeasonAggregations.initiate(
                    {
                      query: { limit: rows.length },
                      body: {
                        dimensions: body.dimensions,
                        filters: {
                          dimension_filters:
                            body.selection.filters?.dimension_filters,
                        },
                        include_list: rows,
                      },
                    },
                    { subscribe: false, forceRefetch: true },
                  ),
                );
                if (error || !data) return;
                if (!edit.isLatest()) return;

                const mergeRefreshedMetrics = mergePatchedMetricsRecipe(
                  transformPreSeasonAggregationResponse(data).items,
                );
                keptQueryArgs.forEach((queryArgs) => {
                  dispatch(
                    assortmentApi.util.updateQueryData(
                      'getPreSeasonAggregationsList',
                      queryArgs,
                      mergeRefreshedMetrics,
                    ),
                  );
                });
              } finally {
                refreshFetchingPatches.forEach((patch) => patch.undo());
              }
            } catch {
              // Any failure leaves the rows as they are
              // the stale-until-refresh behaviour
            }
          }
        } else if (body.selection.selection_type === 'criteria') {
          // criteria selections can touch any row, so refetch everything
          try {
            await queryFulfilled;
            dispatch(
              assortmentApi.util.invalidateTags([
                'PRE_SEASON_AGGREGATIONS_LIST',
              ]),
            );
          } catch (e) {
            recoverFromFailedPreSeasonEdit(dispatch, e);
          }
        }
      },
    }),

    // ========================== IN-SEASON AGGREGATIONS ==========================
    getInSeasonSkuLocationsCount: builder.query<
      GetInSeasonSkuLocationsCountResponse,
      GetInSeasonSkuLocationsCountRequest
    >({
      query: (args) => ({
        ...assortmentQueryBuilder(
          'post',
          '/aggregations/in-season/count',
        )(args),
        extraArgs: { ...args?.extraArgs, hideSnackbar: true },
      }),
    }),
    getInSeasonAggregationsList: builder.infiniteQuery<
      TransformedGetInSeasonAggregationsListResponse,
      TransformedGetInSeasonAggregationsListRequest,
      GetInSeasonAggregationsListInfiniteQueryPageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { cursor: undefined },
        getNextPageParam: (lastPage) => {
          const { next_cursor } = lastPage.page_info;
          return next_cursor ? { cursor: next_cursor } : undefined;
        },
      },
      query: ({ queryArg: { query, ...rest }, pageParam: { cursor } }) => {
        const queryFn = assortmentQueryBuilder(
          'post',
          '/aggregations/in-season',
          transformInSeasonAggregationRequest,
        );
        return {
          ...queryFn({ query: { ...query, cursor }, ...rest }),
          timeout: AGGREGATIONS_LIST_TIMEOUT_MS,
        };
      },
      transformResponse: (response: GetInSeasonAggregationsListResponse) =>
        transformInSeasonAggregationResponse(response),
      providesTags: (result, _, queryArg) => {
        if (!result) return ['IN_SEASON_AGGREGATIONS_LIST'];
        return [
          'IN_SEASON_AGGREGATIONS_LIST',
          {
            type: 'IN_SEASON_AGGREGATIONS_LIST',
            id: JSON.stringify(queryArg),
          },
        ];
      },
    }),
    editInSeasonAggregation: builder.mutation<
      TransformedEditInSeasonAggregationResponse,
      TransformedEditInSeasonAggregationRequest
    >({
      query: (args) => {
        const queryFn = assortmentQueryBuilder(
          'patch',
          '/aggregations/in-season',
          transformEditInSeasonAggregationRequest,
        );
        return {
          ...queryFn(args),
          timeout: AGGREGATIONS_LIST_TIMEOUT_MS,
        };
      },
      transformResponse: (response: EditInSeasonAggregationResponse) =>
        transformEditInSeasonAggregationResponse(response),
      onQueryStarted: async (mutationQueryArgs, mutationLifecycleApi) => {
        const { body } = mutationQueryArgs;
        const { queryFulfilled, dispatch } = mutationLifecycleApi;

        const updateAllInSeasonAggregationsCacheEntries =
          createAllCacheEntriesUpdater(
            assortmentApi,
            'getInSeasonAggregationsList',
            mutationLifecycleApi,
          );

        if (body.selection.selection_type === 'include_list') {
          const selectionIdentifiers = new Set(
            body.selection.include_list.map((item) =>
              JSON.stringify(item.filter(isConcreteDimensionIdentifier)),
            ),
          );
          const patchUpdateResults = updateAllInSeasonAggregationsCacheEntries(
            createUpdateInfiniteDataRecipe(
              createUpdateMultipleItemsRecipe({
                predicate: (item) => selectionIdentifiers.has(item.id),
                updateRecipe: (item) => {
                  if (
                    body.update.update_type === 'initial_allocation_set_each'
                  ) {
                    item.is_fetching_initial_allocation = true;
                  } else {
                    item.is_fetching_assortment_dates = true;
                    item.is_fetching_assortment_status = true;
                  }
                },
              }),
            ),
          );
          try {
            const { data } = await queryFulfilled;
            const patchedItems = data.items;
            const patchedItemsMap = new Map(
              patchedItems.map((item) => [item.id, item]),
            );
            updateAllInSeasonAggregationsCacheEntries(
              createUpdateInfiniteDataRecipe(
                createUpdateMultipleItemsRecipe({
                  predicate: (item) => patchedItemsMap.has(item.id),
                  updateRecipe: (item) => {
                    item.metrics = patchedItemsMap.get(item.id)!.metrics;
                  },
                }),
              ),
            );

            // invalidate all cache entries except the current one
            const inSeasonCachedQueryArgs =
              assortmentApi.util.selectCachedArgsForQuery(
                mutationLifecycleApi.getState(),
                'getInSeasonAggregationsList',
              );
            const inSeasonCachedQueryArgsToInvalidate =
              findInSeasonQueryArgsToInvalidate(
                inSeasonCachedQueryArgs,
                mutationQueryArgs,
              ).map((queryArgs) => JSON.stringify(queryArgs));
            dispatch(
              assortmentApi.util.invalidateTags(
                inSeasonCachedQueryArgsToInvalidate.map((id) => ({
                  type: 'IN_SEASON_AGGREGATIONS_LIST',
                  id,
                })),
              ),
            );
          } catch (e) {
            const isTimeoutError = isQueryFulfilledRejectionTimeoutError(e);
            if (!isTimeoutError) {
              mutationLifecycleApi.dispatch(
                assortmentApi.util.invalidateTags([
                  'IN_SEASON_AGGREGATIONS_LIST',
                ]),
              );
            }
          } finally {
            patchUpdateResults.forEach((patch) => patch.undo());
          }
        } else if (body.selection.selection_type === 'criteria') {
          // if the selection is criteria, invalidate all cache entries and refetch
          try {
            await queryFulfilled;
            mutationLifecycleApi.dispatch(
              assortmentApi.util.invalidateTags([
                'IN_SEASON_AGGREGATIONS_LIST',
              ]),
            );
          } catch (e) {
            const isTimeoutError = isQueryFulfilledRejectionTimeoutError(e);
            if (!isTimeoutError) {
              mutationLifecycleApi.dispatch(
                assortmentApi.util.invalidateTags([
                  'IN_SEASON_AGGREGATIONS_LIST',
                ]),
              );
            }
          }
        }
      },
    }),
  }),
});

// ============ editPreSeasonAggregation lifecycle helpers ============

type AssortmentApiState = Parameters<
  ReturnType<typeof assortmentApi.endpoints.getPreSeasonAggregationsList.select>
>[0];

type AssortmentLifecycleApi = {
  getState: () => AssortmentApiState;
  dispatch: ThunkDispatch<AssortmentApiState, unknown, UnknownAction>;
};

/** Invalidate the cached views the edit's pessimistic merge cannot keep
 * correct (different dimensions or dimension filters); the matching views
 * are updated in place instead. */
const invalidateStalePreSeasonViews = (
  { getState, dispatch }: AssortmentLifecycleApi,
  mutationQueryArgs: TransformedEditPreSeasonAggregationRequest,
): void => {
  const cachedQueryArgs = assortmentApi.util.selectCachedArgsForQuery(
    getState(),
    'getPreSeasonAggregationsList',
  );
  const staleQueryArgs = findPreSeasonQueryArgsToInvalidate(
    cachedQueryArgs,
    mutationQueryArgs,
  );
  dispatch(
    assortmentApi.util.invalidateTags(
      staleQueryArgs.map((queryArgs) => ({
        type: 'PRE_SEASON_AGGREGATIONS_LIST' as const,
        id: JSON.stringify(queryArgs),
      })),
    ),
  );
};

const recoverFromFailedPreSeasonEdit = (
  dispatch: AssortmentLifecycleApi['dispatch'],
  error: unknown,
): void => {
  const isTimeoutError = isQueryFulfilledRejectionTimeoutError(error);
  const is400Error =
    isQueryFulfilledRejectionReason<EditPreSeasonAggregationError>(error) &&
    error.error.status === 400;
  const shouldRefetch = !isTimeoutError && !is400Error;
  if (shouldRefetch) {
    dispatch(
      assortmentApi.util.invalidateTags(['PRE_SEASON_AGGREGATIONS_LIST']),
    );
  }
};

// Edits are debounced but can still overlap an in-flight refresh; only the
// latest edit's refresh may merge, so a stale response can never overwrite
// fresher data.
let latestPreSeasonEditId = 0;

type PreSeasonEdit = { isLatest: () => boolean };

const beginPreSeasonEdit = (): PreSeasonEdit => {
  const id = ++latestPreSeasonEditId;
  return { isLatest: () => id === latestPreSeasonEditId };
};

export const {
  useCreateInitialAllocationRecommendationsJobMutation,
  useGetInSeasonAggregationsListInfiniteQuery,
  useGetInSeasonSkuLocationsCountQuery,
  useGetLatestInitialAllocationRecommendationsJobQuery,
  useGetPreSeasonAggregationsListInfiniteQuery,
  useGetPreSeasonSkuLocationsCountQuery,
  useEditInSeasonAggregationMutation,
  useEditPreSeasonAggregationMutation,
  useGetCustomerDataFeedStatusQuery,
} = assortmentApi;
